/**
 * Home Component
 *
 * This is the main landing page component that displays the Northern Canada Map
 * and provides a collapsible drawer for station list navigation.
 *
 * Key Features:
 * - Responsive layout that adapts to different screen sizes
 * - Interactive map display
 * - Collapsible side drawer for station list
 * - Pin functionality to keep drawer open
 * - Backdrop overlay for better UX
 */

import React, { useState } from "react";
import {
  Typography,
  Box,
  IconButton,
  Drawer,
  Backdrop,
  useMediaQuery,
  Tooltip,
  Chip,
  Link,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PushPinIcon from "@mui/icons-material/PushPin";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import NorthernCanadaMap from "../Map/NorthernCanadaMap";
import AllStationsList from "../AllStations/AllStationsList";
import { useNavigate } from "react-router-dom";

function Home() {
  // State management for drawer visibility and pin status
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const navigate = useNavigate();

  // Responsive design: Adjust drawer width based on screen size
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const drawerWidth = isSmallScreen ? "70%" : "350px";

  // Toggle drawer visibility
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Toggle pin status to keep drawer open
  const togglePin = () => {
    setPinned(!pinned);
  };

  return (
    <>
      {/* Main container with map */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          position: "relative",
        }}
      >
        {/* Wind Map Information */}
        <Box
          sx={{
            width: "100%",
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <Chip
              label="NEW FEATURE"
              size="small"
              sx={{
                backgroundColor: "#4caf50",
                color: "white",
                fontWeight: "bold",
                fontSize: "0.7rem",
              }}
            />
            <Typography variant="body2">Wind Map now available -</Typography>
            <Link
              onClick={() => navigate("/wind")}
              sx={{
                cursor: "pointer",
                fontWeight: 500,
                textDecoration: "none",
                color: "#1976d2",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Click here
            </Link>
          </Box> */}
        </Box>

        {/* Map container */}
        <Box
          sx={{ pr: 2, pl: 2, pb: 2, width: "100%", height: "100%", flex: 1 }}
        >
          <NorthernCanadaMap />
        </Box>

        {/* Expand menu button with tooltip */}
        <Tooltip title="Expand Menu" placement="left">
          <IconButton
            onClick={toggleDrawer}
            sx={{
              position: "fixed",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              width: "30px",
              height: "80px",
              backgroundColor: "black",
              color: "white",
              borderRadius: "5px 0 0 5px",
              "&:hover": {
                backgroundColor: "black",
              },
              zIndex: 1200,
            }}
          >
            <ArrowLeftIcon />
          </IconButton>
        </Tooltip>

        {/* Backdrop overlay when drawer is open */}
        <Backdrop
          open={drawerOpen && !pinned}
          onClick={toggleDrawer}
          sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}
        />

        {/* Side drawer for station list */}
        <Drawer
          anchor="right"
          open={drawerOpen || pinned}
          onClose={toggleDrawer}
          variant="persistent"
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            {/* Drawer controls */}
            <IconButton onClick={toggleDrawer}>
              <ChevronRightIcon />
            </IconButton>
            <IconButton onClick={togglePin}>
              <PushPinIcon color={pinned ? "primary" : "inherit"} />
            </IconButton>
            {/* Station list component */}
            <AllStationsList />
          </Box>
        </Drawer>
      </Box>
    </>
  );
}

export default Home;
